import type { z } from 'zod';
import { useConnectivityStore } from '@/stores/connectivity-store';
import { kioskConfig } from '@/lib/config/kiosk-config';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'DELETE';
  body?: unknown;
  /** Sent as Idempotency-Key — required for mutations (double-tap safety). */
  idempotencyKey?: string;
  signal?: AbortSignal;
};

/**
 * Thin fetch wrapper: injects the kiosk token + idempotency key, parses the
 * response, and validates it against a Zod schema so contract drift throws loudly.
 */
export async function request<T>(
  path: string,
  schema: z.ZodType<T>,
  opts: RequestOptions = {},
): Promise<T> {
  // Read per-call, not at module scope: the config is loaded asynchronously at
  // boot, so a module-level const would capture a value that doesn't exist yet.
  const headers: Record<string, string> = {
    'X-Kiosk-Token': kioskConfig().kioskToken,
  };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;

  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (err) {
    // Network-level failure (offline, DNS, etc.)
    throw new ApiError(0, err instanceof Error ? err.message : 'Network error');
  }

  const text = await res.text();
  const json: unknown = text ? JSON.parse(text) : null;

  if (!res.ok) {
    throw new ApiError(res.status, `Request failed: ${res.status}`, json);
  }

  useConnectivityStore.getState().markApiSuccess();
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    // Contract drift: name the offending fields so the kiosk console/Sentry
    // shows more than a generic error toast.
    console.error(`[api] response validation failed for ${path}`, parsed.error.issues);
    throw parsed.error;
  }
  return parsed.data;
}
